import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "motion/react";
import { Sprout, RefreshCw, Sparkles, BookOpen, Compass } from "lucide-react";

// Curated rotation of inspiring botanical wisdom/facts for a premium user experience
const BOTANICAL_MESSAGES = [
  "Consulting botanical taxonomic databases...",
  "Analyzing leaf venation and chlorophyll signatures...",
  "Connecting with global botanical herbaria...",
  "Calibrating horticultural computer vision models...",
  "Verifying USDA climate hardiness zone parameters...",
  "Fetching local soil pH and irrigation guidelines...",
  "Decoding plant taxonomy and historical profiles...",
  "Loading high-definition foliage assets..."
];

const ADMIN_MESSAGES = [
  "Synchronizing secure administrative cloud infrastructure...",
  "Authorizing enterprise credential pathways...",
  "Querying secure database indexes and metrics...",
  "Calibrating neural classification networks...",
  "Retrieving real-time botanical intelligence telemetry..."
];

/**
 * ProfessionalLoader: A gorgeous, highly-polished fallback loader
 * featuring smooth micro-animations, rotating botanical wisdom, and a glowing ring.
 */
export function ProfessionalLoader() {
  const [messageIndex, setMessageIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setMessageIndex((prev) => (prev + 1) % BOTANICAL_MESSAGES.length);
    }, 2800);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="min-h-[55vh] w-full flex flex-col items-center justify-center p-8 bg-linear-to-b from-green-50/20 to-white/40 backdrop-blur-xs rounded-2xl relative overflow-hidden">
      {/* Decorative background blur nodes */}
      <div className="absolute top-1/4 left-1/4 w-32 h-32 bg-emerald-100/30 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-1/4 right-1/4 w-32 h-32 bg-teal-100/30 rounded-full blur-3xl pointer-events-none" />

      <div className="relative flex flex-col items-center max-w-sm text-center z-10">
        {/* Animated Icon Ring */}
        <div className="relative w-20 h-20 flex items-center justify-center mb-6">
          {/* Outer rotating dashed ring */}
          <motion.div
            className="absolute inset-0 border-2 border-dashed border-emerald-500/30 rounded-full"
            animate={{ rotate: 360 }}
            transition={{ duration: 12, repeat: Infinity, ease: "linear" }}
          />

          {/* Inner spinning solid ring with gradient */}
          <motion.div
            className="absolute inset-1.5 border-3 border-transparent border-t-emerald-600 border-r-teal-500 rounded-full"
            animate={{ rotate: -360 }}
            transition={{ duration: 1.8, repeat: Infinity, ease: "linear" }}
          />

          {/* Core Sprout Icon pulsing gently */}
          <motion.div
            className="w-12 h-12 rounded-full bg-emerald-50 flex items-center justify-center shadow-xs"
            animate={{ scale: [1, 1.08, 1] }}
            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
          >
            <Sprout className="w-6 h-6 text-emerald-600" />
          </motion.div>

          {/* Sparkle decorative micro-badge */}
          <motion.div
            className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-teal-500 flex items-center justify-center text-white shadow-xs"
            animate={{ y: [0, -3, 0], opacity: [0.7, 1, 0.7] }}
            transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
          >
            <Sparkles className="w-3 h-3" />
          </motion.div>
        </div>

        {/* Shimmering Animated Title */}
        <h3 className="text-sm font-semibold text-emerald-950 uppercase tracking-widest mb-2 flex items-center gap-1.5 justify-center">
          <Compass className="w-4 h-4 text-emerald-600 animate-spin" style={{ animationDuration: "6s" }} />
          Plant Identifier
        </h3>

        {/* Rotating botanical wisdom text with sliding fade effect */}
        <div className="h-12 flex items-center justify-center overflow-hidden px-4">
          <AnimatePresence mode="wait">
            <motion.p
              key={messageIndex}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.35, ease: "easeInOut" }}
              className="text-xs text-emerald-800 font-medium leading-relaxed max-w-xs"
            >
              {BOTANICAL_MESSAGES[messageIndex]}
            </motion.p>
          </AnimatePresence>
        </div>

        {/* Tactile micro progress line */}
        <div className="w-36 h-1 bg-emerald-100 rounded-full mt-4 overflow-hidden relative">
          <motion.div
            className="absolute top-0 bottom-0 left-0 bg-gradient-to-r from-emerald-500 to-teal-400 rounded-full"
            animate={{ 
              left: ["-100%", "100%"] 
            }}
            transition={{ 
              duration: 1.8, 
              repeat: Infinity, 
              ease: "easeInOut" 
            }}
            style={{ width: "50%" }}
          />
        </div>
      </div>
    </div>
  );
}

/**
 * AdminProfessionalLoader: An elegant dark-mode styled fallback loader
 * for the security/administration portal.
 */
export function AdminProfessionalLoader() {
  const [messageIndex, setMessageIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setMessageIndex((prev) => (prev + 1) % ADMIN_MESSAGES.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="min-h-[55vh] w-full flex flex-col items-center justify-center p-8 bg-[#111612]/75 backdrop-blur-xs border border-emerald-950/40 rounded-2xl relative overflow-hidden">
      {/* Matrix-like tech nodes in background */}
      <div className="absolute top-1/3 left-1/3 w-24 h-24 bg-emerald-500/5 rounded-full blur-2xl pointer-events-none" />
      <div className="absolute bottom-1/3 right-1/3 w-24 h-24 bg-teal-500/5 rounded-full blur-2xl pointer-events-none" />

      <div className="relative flex flex-col items-center max-w-sm text-center z-10">
        {/* Animated Icon Ring */}
        <div className="relative w-20 h-20 flex items-center justify-center mb-6">
          <motion.div
            className="absolute inset-0 border border-dashed border-emerald-500/20 rounded-full"
            animate={{ rotate: 360 }}
            transition={{ duration: 15, repeat: Infinity, ease: "linear" }}
          />

          <motion.div
            className="absolute inset-1.5 border-2 border-transparent border-t-emerald-500 border-r-teal-400 rounded-full"
            animate={{ rotate: -360 }}
            transition={{ duration: 1.5, repeat: Infinity, ease: "linear" }}
          />

          <motion.div
            className="w-12 h-12 rounded-full bg-emerald-950/50 border border-emerald-500/20 flex items-center justify-center shadow-inner"
            animate={{ scale: [1, 1.05, 1] }}
            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
          >
            <RefreshCw className="w-5 h-5 text-emerald-400 animate-spin" style={{ animationDuration: "4s" }} />
          </motion.div>
        </div>

        {/* Title */}
        <h3 className="text-xs font-mono font-bold text-emerald-450 uppercase tracking-widest mb-2">
          [ SYSTEM CONTROL PORTAL ]
        </h3>

        {/* Message */}
        <div className="h-12 flex items-center justify-center overflow-hidden px-4">
          <AnimatePresence mode="wait">
            <motion.p
              key={messageIndex}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
              className="text-xs text-emerald-400/85 font-mono tracking-tight leading-relaxed"
            >
              {ADMIN_MESSAGES[messageIndex]}
            </motion.p>
          </AnimatePresence>
        </div>

        {/* Tech Line */}
        <div className="w-40 h-[2px] bg-emerald-950 rounded-full mt-4 overflow-hidden relative">
          <motion.div
            className="absolute top-0 bottom-0 left-0 bg-emerald-500 rounded-full shadow-[0_0_8px_rgba(16,185,129,0.5)]"
            animate={{ 
              left: ["-100%", "100%"] 
            }}
            transition={{ 
              duration: 2, 
              repeat: Infinity, 
              ease: "easeInOut" 
            }}
            style={{ width: "40%" }}
          />
        </div>
      </div>
    </div>
  );
}

/**
 * RouteProgressBar: An instant-feedback horizontal progress bar
 * mounted on top of the viewport during route transitions.
 */
export function RouteProgressBar({ active }: { active: boolean }) {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    let interval: any;
    if (active) {
      setProgress(15);
      interval = setInterval(() => {
        setProgress((prev) => {
          if (prev >= 85) {
            clearInterval(interval);
            return prev;
          }
          // Slow down as it approaches 85%
          const increment = prev < 50 ? 12 : prev < 75 ? 6 : 2;
          return prev + increment;
        });
      }, 100);
    } else {
      setProgress(100);
      const timeout = setTimeout(() => {
        setProgress(0);
      }, 400);
      return () => clearTimeout(timeout);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [active]);

  if (progress === 0) return null;

  return (
    <div 
      className="fixed top-0 left-0 right-0 h-[4px] z-55 pointer-events-none transition-opacity duration-300"
      style={{ opacity: progress === 100 ? 0 : 1 }}
    >
      <div
        className="h-full bg-gradient-to-r from-emerald-600 via-green-500 to-teal-400 shadow-[0_1px_10px_rgba(16,185,129,0.3)] transition-all duration-300"
        style={{ width: `${progress}%` }}
      />
    </div>
  );
}
